import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './PlansManager.css';

import backIcon from '../../assets/backspace-icon.png';
import api from '../../api';

function formatPct(x) {
  if (typeof x !== 'number' || Number.isNaN(x)) return '0.00%';
  return `${x.toFixed(2)}%`;
}



function PlanRow({ label, planId, count, percent, totalUsers }) {
  const pctNum = typeof percent === 'number' ? percent : 0;
  const width = Math.max(0, Math.min(100, pctNum));

  return (
    <div className="plans-row">
      <div className="plans-row-left">
        {/* Plan label */}
        <div className={`plans-pill plans-pill-${planId}`}>{label}</div>

        {/* Count line */}
        <div className="plans-row-meta">
          <span>{count} user{count === 1 ? '' : 's'}</span>
          <span className="plans-row-dot">•</span>
          <span>of {totalUsers}</span>
        </div>

        {/* Percentage */}
        <div className="plans-row-pct">{formatPct(pctNum)}</div>

        {/* Progress bar */}
        <div className="plans-bar">
          <div className="plans-bar-fill" style={{ width: `${width}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function PlansManager() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const rows = useMemo(() => {
    const byPlan = payload?.byPlan || [];
    const order = ['FREE', 'BASIC', 'PREMIUM', 'PREMIUM_PLUS'];
    const map = new Map(byPlan.map((r) => [r.planId, r]));
    return order.map((id) => map.get(id) || { planId: id, label: id, count: 0, percent: 0 });
  }, [payload]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await api.get('/admin/metrics/plans');
        if (!mounted) return;
        setPayload(data);
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load plan stats');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="plans-screen">
      <div className="plans-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="plans-back"
          onClick={() => navigate('/admin')}
        />
        <div className="plans-title-wrap">
          <div className="plans-title">Plans</div>
        </div>
      </div>

      {/* ✅ Scrollable content */}
      <div className="plans-body">
        {loading ? (
          <div className="plans-card">
            <div className="plans-skel-title" />
            <div className="plans-skel-row" />
            <div className="plans-skel-row" />
            <div className="plans-skel-row" />
            <div className="plans-skel-row" />
          </div>
        ) : error ? (
          <div className="plans-error">
            <div className="plans-error-title">Couldn’t load plan stats</div>
            <div className="plans-error-text">{error}</div>
            <button className="plans-btn" onClick={() => window.location.reload()} type="button">
              Try again
            </button>
          </div>
        ) : (
          <>
            {/* ✅ Summary only: Total Users + Active Paid */}
            <div className="plans-summary">
              <div className="plans-summary-card">
                <div className="plans-summary-label">Total Users</div>
                <div className="plans-summary-value">{payload?.totalUsers ?? 0}</div>
              </div>

              <div className="plans-summary-card">
                <div className="plans-summary-label">Active Paid</div>
                <div className="plans-summary-value">{payload?.totalActivePaid ?? 0}</div>
              </div>
            </div>

            <div className="plans-card">
              <div className="plans-card-title">Plan breakdown</div>

              {rows.map((r) => (
                <PlanRow
                  key={r.planId}
                  planId={r.planId}
                  label={r.label || r.planId}
                  count={r.count || 0}
                  percent={r.percent || 0}
                  totalUsers={payload?.totalUsers || 0}
                />
              ))}

              <div className="plans-note">
                * “Active Paid” counts users with an active, non-expired BASIC / PREMIUM / PREMIUM_PLUS plan record.
                Everyone else is treated as Free.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
